import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { InputNumberModule } from 'primeng/inputnumber';
import { DropdownModule } from 'primeng/dropdown';
import { TranslateModule } from '@ngx-translate/core';
import { MovementLocation } from 'src/app/models/movement/movement-location';

/**
 * EM-2589: formulário de vaga do mapa de vagas (nova vaga e alteração), no lugar do Swal.
 *
 * Devolve no `close` os valores do formulário: `movementLocation` (objeto do local), `code` (sem o prefixo do
 * local; o backend acrescenta a abreviação quando o local usa prefixo), `maxBoatLength` (pés, inteiro como a
 * coluna `vacancy.length`), `berthMapWidth`/`berthMapHeight` (metros, só desenho no mapa), `x`/`y` do clique e
 * `vacancyChanged` (na alteração, se código, tamanho máximo ou local mudaram; só largura/comprimento não vão para o
 * backend).
 */
@Component({
  selector: 'app-berth-map-vacancy-form',
  templateUrl: './berth-map-vacancy-form.component.html',
  styleUrls: ['./berth-map-vacancy-form.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    InputTextModule,
    ButtonModule,
    InputNumberModule,
    DropdownModule,
    TranslateModule
  ]
})
export class BerthMapVacancyFormComponent implements OnInit {
  movementLocations: MovementLocation[] = [];
  vacancyForm: FormGroup;
  x: number;
  y: number;
  isEditMode = false;
  /** Vaga antiga cujo código não tem o prefixo do local (o backend acrescenta o prefixo em todo PUT). */
  codeWithoutLocationPrefix: { code: string; prefix: string } | null = null;

  private initial: { code: string; length: number | null; locationId: number | null };

  constructor(
    private ref: DynamicDialogRef,
    private config: DynamicDialogConfig,
    private fb: FormBuilder,
  ) {}

  ngOnInit(): void {
    const data = this.config.data || {};
    this.movementLocations = data.movementLocations || [];
    this.x = data.x || 0;
    this.y = data.y || 0;
    this.isEditMode = !!data.vacancy;

    const vacancy = data.vacancy || {};
    const currentLocation = this.findLocation(vacancy.movementLocation);

    this.vacancyForm = this.fb.group({
      // Sem local de vaga molhada cadastrado a vaga sai sem local, como no Swal da stable.
      movementLocation: [currentLocation, this.movementLocations.length > 0 ? Validators.required : []],
      code: [this.codeWithoutPrefix(vacancy.code, vacancy.movementLocation || currentLocation), requiredTrimmed],
      maxBoatLength: [vacancy.length ?? null, [Validators.required, Validators.min(0)]],
      berthMapWidth: [data.berthMapWidth ?? null, [Validators.required, Validators.min(0.1)]],
      berthMapHeight: [data.berthMapHeight ?? null, [Validators.required, Validators.min(0.1)]]
    });

    // Mesma regra do cadastro de vagas: vaga existente em local ativo não troca de local.
    if (this.isEditMode && currentLocation && currentLocation.active !== false) {
      this.vacancyForm.get('movementLocation').disable();
    }

    this.initial = {
      code: this.normalizeCode(this.vacancyForm.get('code').value),
      length: this.normalizeNumber(vacancy.length),
      locationId: currentLocation?.id ?? null
    };
    const original = vacancy.movementLocation || currentLocation;
    if (this.isEditMode && original?.prefixUse && original.abbreviation && vacancy.code
        && !String(vacancy.code).startsWith(original.abbreviation)) {
      this.codeWithoutLocationPrefix = { code: vacancy.code, prefix: original.abbreviation };
    }
  }

  get selectedLocation(): MovementLocation | null {
    return this.vacancyForm?.get('movementLocation').value || null;
  }

  /** Como no cadastro de vagas: 4 caracteres quando o local usa prefixo, senão 7. */
  get codeMaxLength(): number {
    return this.selectedLocation?.prefixUse ? 4 : 7;
  }

  save(): void {
    if (this.vacancyForm.invalid) {
      this.vacancyForm.markAllAsTouched();
      return;
    }
    const formValue = this.vacancyForm.getRawValue();
    const code = this.normalizeCode(formValue.code);
    const vacancyChanged = !this.isEditMode
      || code !== this.initial.code
      || this.normalizeNumber(formValue.maxBoatLength) !== this.initial.length
      || (formValue.movementLocation?.id ?? null) !== this.initial.locationId;
    this.ref.close({
      ...formValue,
      code,
      x: this.x,
      y: this.y,
      vacancyChanged
    });
  }

  onCancel(): void {
    this.ref.close();
  }

  private findLocation(location: MovementLocation | null | undefined): MovementLocation | null {
    if (!location || location.id == null) {
      return null;
    }
    return this.movementLocations.find((l) => l.id === location.id) || null;
  }

  private normalizeCode(code: any): string {
    return code == null ? '' : String(code).trim();
  }

  private normalizeNumber(value: any): number | null {
    return value === null || value === undefined || value === '' ? null : Number(value);
  }

  private codeWithoutPrefix(code: string, location: MovementLocation | null | undefined): string {
    if (!code) {
      return '';
    }
    const prefix = location?.prefixUse ? location.abbreviation : null;
    return prefix && code.startsWith(prefix) ? code.substring(prefix.length) : code;
  }
}

/** `required` que não aceita só espaços (o backend recusa código em branco depois do trim). */
function requiredTrimmed(control: AbstractControl): ValidationErrors | null {
  return control.value == null || String(control.value).trim() === '' ? { required: true } : null;
}
